import React, { useState, useEffect, useContext } from "react";
import { FaFilePdf, FaEye, FaPlusCircle } from "react-icons/fa";
import { Dropdown, Table, Button, Modal, Form } from "react-bootstrap";
import { FaChevronDown } from "react-icons/fa";
import {
  BASE_URL,
  GetAllApprovalBatchDatafromSubmit,
  updateInvoiceStatusNetworkForApproved,
  insertPaymentDetails,
  saveBatchPayment,
} from "../../api/api";
import { toast, ToastContainer } from "react-toastify";
import pdfimage from "../images/pdf_downlaod.png";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import DescriptionIcon from "@mui/icons-material/Description";
import { IconButton } from "@mui/material";
import { statusColor } from "../../utils/statusColor";

function ApprovedBatches() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("Processed");
  const [invoices, setInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useContext(AuthContext);
  const serviceProviderName = localStorage.getItem("ServiceProviderName");

  // Payment Details Popup State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    paymentDate: "",
    transactionNo: "",
    bankRefNo: "",
    amountPaid: "",
    paymentMode: "NEFT",
    remarks: "",
  });
  const [formErrors, setFormErrors] = useState({
    paymentDate: false,
    transactionNo: false,
    bankRefNo: false,
    amountPaid: false,
    paymentMode: false,
    remarks: false,
  });

  const fetchData = async () => {
    try {
      const response = await fetch(`${BASE_URL}/GetFinanceReviewBatches`);
      const result = await response.json();
      setInvoices(result.dataItems || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  const formatDate = (dateTimeStr) => {
    if (!dateTimeStr || typeof dateTimeStr !== "string") {
      console.warn("Invalid date input:", dateTimeStr);
      return "--";
    }

    if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(dateTimeStr)) {
      return dateTimeStr.replace(/\//g, "-");
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateTimeStr)) {
      try {
        const date = new Date(dateTimeStr);
        if (isNaN(date.getTime())) {
          console.warn("Invalid YYYY-MM-DD date:", dateTimeStr);
          return "--";
        }
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      } catch (e) {
        console.warn("Error parsing YYYY-MM-DD date:", dateTimeStr, e);
        return "--";
      }
    }

    // Case 3: Custom format like "2025 15:37:37-04-21"
    if (/\d{4}\s+\d{2}:\d{2}:\d{2}-\d{2}-\d{2}/.test(dateTimeStr)) {
      try {
        const [year, timeAndDate] = dateTimeStr.split(" ");
        const [, , dayMonth] = timeAndDate.split(":");
        const [, month, day] = dayMonth.split("-");
        const formattedDate = `${day}-${month}-${year}`;
        // Validate the constructed date
        const date = new Date(`${year}-${month}-${day}`);
        if (isNaN(date.getTime())) {
          console.warn("Invalid custom format date:", dateTimeStr);
          return "--";
        }
        return formattedDate;
      } catch (e) {
        console.warn("Error parsing custom format date:", dateTimeStr, e);
        return "--";
      }
    }

    // Fallback for any other format: Try parsing with Date
    try {
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) {
        console.warn("Unrecognized date format:", dateTimeStr);
        return "--";
      }
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      console.warn("Error parsing date with Date object:", dateTimeStr, e);
      return "--";
    }
  };
  const filteredInvoices = invoices.filter((invoice) => {
    const isNetworkRole =
      user?.role === "NetworkAdmin" ||
      user?.role === "NetworkSubAdmin" ||
      user?.role === "finance" ||
      user?.role === "Finance";

    const serviceProviderMatch = isNetworkRole
      ? true
      : serviceProviderName
      ? invoice.vendorName?.toLowerCase() === serviceProviderName.toLowerCase()
      : true;

    if (!serviceProviderMatch) {
      return false;
    }

    const statusVal = invoice.invoiceStatus?.trim().toLowerCase() || "";
    const filterVal = statusFilter.toLowerCase();
    if (filterVal === "processed") {
      return statusVal === "processed" || statusVal === "paid" || statusVal === "payment done";
    }
    if (filterVal === "paid") {
      return statusVal === "paid" || statusVal === "payment done";
    }
    return statusVal === filterVal;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, serviceProviderName]);

  const itemsPerPage = 10;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInvoices = filteredInvoices.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const handleSort = (key) => {
    setSortConfig((prev) => {
      const isSameKey = prev.key === key;
      return {
        key,
        direction: isSameKey && prev.direction === "asc" ? "desc" : "asc",
      };
    });
  };
  const sortedInvoices = [...currentInvoices].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;

    const valA = a[key] ?? "";
    const valB = b[key] ?? "";

    if (typeof valA === "number" && typeof valB === "number") {
      return direction === "asc" ? valA - valB : valB - valA;
    }

    return direction === "asc"
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "batch":
      case "hold":
        return "batch_badge_orange";
      case "approved":
        return "approved_badge_green";
      case "submitted":
        return "submitted_badge_red";
      case "invoice":
        return "invoice_badge_blue";
      case "rejected":
        return "rejected_badge_red";
      case "paid":
        return "paid_badge_red";
      case "query":
        return "query_badge_red";
      case "partial payment":
        return "partial_badge_red";
      case "partial approved":
        return "partial_approved_badge_red";
      case "bank":
        return "bank_badge";
      default:
        return "badge-gray";
    }
  };

  const invoiceOptions = ["Approve", "Reject"];

  const handleInvoiceStatusChange = async (index, newStatus) => {
    const globalIndex = indexOfFirstItem + index;
    const selectedInvoice = filteredInvoices[globalIndex];
    const batchNo = selectedInvoice?.batchNo;
    try {
      const response = await updateInvoiceStatusNetworkForApproved({
        batchNo,
        invoiceStatus: newStatus,
      });
      setInvoices((prev) =>
        prev.map((item) =>
          item.batchNo === batchNo
            ? {
              ...item,
              invoiceStatus: newStatus,
              financeStatus:
                newStatus === "Approved" ? "Submitted" : item.financeStatus,
            }
            : item
        )
      );

      if (newStatus === "Approve") {
        navigate("/ApprovedBatchData", {
          state: {
            batchData: selectedInvoice,
            batchNo: batchNo,
            invoiceStatus: newStatus,
          },
        });
      }
    } catch (err) {
      console.error("Failed to update invoice status:", err);
      toast.error("Failed to update invoice status");
    }
  };

  const handleAddDetailsClick = (invoice) => {
    setSelectedBatch(invoice);

    let formattedPaymentDate = "";
    if (invoice.paymentDate) {
      formattedPaymentDate = invoice.paymentDate.split("T")[0];
    }

    setPaymentForm({
      paymentDate: formattedPaymentDate,
      transactionNo: invoice.transactionNumber || invoice.transactionNo || "",
      bankRefNo: invoice.bankRefNumber || invoice.bankRefNo || "",
      amountPaid: invoice.amountPaid || "",
      paymentMode: invoice.paymentMode || "NEFT",
      remarks: invoice.remarks || "",
    });
    setFormErrors({
      paymentDate: false,
      transactionNo: false,
      bankRefNo: false,
      amountPaid: false,
      paymentMode: false,
      remarks: false,
    });
    setShowPaymentModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    const errors = {
      paymentDate: !paymentForm.paymentDate,
      transactionNo: !paymentForm.transactionNo,
      bankRefNo: !paymentForm.bankRefNo,
      amountPaid: !paymentForm.amountPaid,
      paymentMode: !paymentForm.paymentMode,
      remarks: !paymentForm.remarks,
    };

    setFormErrors(errors);

    if (Object.values(errors).some(Boolean)) {
      toast.error("Please fill in all mandatory fields.");
      return;
    }
    try {
      const payload = {
        batchNumber: selectedBatch?.batchNo?.toString() || "",
        invoiceNumber: selectedBatch?.invoiceNo || "",
        paymentDate: paymentForm.paymentDate,
        transactionNumber: paymentForm.transactionNo,
        bankRefNumber: paymentForm.bankRefNo,
        amountPaid: paymentForm.amountPaid?.toString() || "",
        paymentMode: paymentForm.paymentMode,
        remarks: paymentForm.remarks,
      };

      const result = await saveBatchPayment(payload);
      if (result.status === true || result.status === "Success" || result.ok) {
        toast.success("Payment details saved successfully.");
        setShowPaymentModal(false);
        fetchData();
      } else {
        toast.error(result.message || "Failed to save payment details.");
      }
    } catch (error) {
      console.error("Error saving payment details:", error);
      toast.error("Failed to save payment details. Please try again.");
    }
  };

  const isFinance = user?.role === "finance" || user?.role === "Finance";

  return (
    <div>
      <ToastContainer />
      <div className={isFinance ? "container-fluid px-4 mt-4" : "container mt-4"}>
        <div className="netwrok_table_main_content">
          <div className="d-flex justify-content-between align-items-center network_filter_div">
            <h5 className="fw-bold m-0">Approved Data</h5>
          </div>
          <div className="table-responsive mt-3">
            <div className="d-flex justify-content-between network_filter_div mb-3 align-items-center">
              <div className="d-flex align-items-center">
                <Form.Label className="fw-bold m-0 me-2" style={{ whiteSpace: "nowrap" }}>Status:</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ width: "200px", borderRadius: "8px", border: "1px solid #ced4da", padding: "8px 12px" }}
                >
                  <option value="Processed">Processed</option>
                  <option value="Paid">Paid</option>
                </Form.Select>
              </div>
            </div>
            <Table className="bg-white text-center border-0 network_table">
              <thead style={{ backgroundColor: "#EEF4FF" }}>
                <tr className="text-dark fw-semibold table_th_border">
                  <th className="border-start" style={{ whiteSpace: "nowrap" }}>
                    View
                  </th>
                  {user?.role === "finance" && (
                    <th style={{ whiteSpace: "nowrap" }}>Add Details</th>
                  )}
                  <th style={{ whiteSpace: "nowrap" }}>Log</th>
                  <th onClick={() => handleSort("batchNo")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                    Batch no {sortConfig.key === "batchNo" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th onClick={() => handleSort("vendorName")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                    Vendor Name {sortConfig.key === "vendorName" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th onClick={() => handleSort("caseCount")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                    Case Count {sortConfig.key === "caseCount" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th onClick={() => handleSort("totalRepairCharges")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                    Total Repair Charges {sortConfig.key === "totalRepairCharges" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th onClick={() => handleSort("totalServiceCharges")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                    Total Service Charges {sortConfig.key === "totalServiceCharges" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th onClick={() => handleSort("gst")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                    GST {sortConfig.key === "gst" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th onClick={() => handleSort("finalAmount")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                    Total {sortConfig.key === "finalAmount" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th>Invoice</th>
                  <th onClick={() => handleSort("invoiceStatus")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                    Invoice Status {sortConfig.key === "invoiceStatus" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                  </th>
                  {/* <th onClick={() => handleSort("networkStatus")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                    Finance Status {sortConfig.key === "networkStatus" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
                  </th> */}
                </tr>
              </thead>
              <tbody>
                {sortedInvoices.length > 0 ? (
                  sortedInvoices.map((invoice, index) => (
                    <tr
                      key={invoice.id || `${invoice.batchNo}-${index}`}
                      className="text-center border-bottom network_td_item"
                    >
                      <td className="border-start align-middle cursor-pointer">
                        <FaEye
                          size={20}
                          className="text-purple review_fa_eye"
                          onClick={() => {
                            navigate("/ApprovedBatchData", {
                              state: { batchData: invoice },
                            });
                          }}
                        />
                      </td>
                      {user?.role === "finance" && (
                        <td className="align-middle cursor-pointer">
                          <FaPlusCircle
                            size={20}
                            className="text-purple review_fa_eye"
                            onClick={() => handleAddDetailsClick(invoice)}
                            title="Add Details"
                          />
                        </td>
                      )}
                      <td className="align-middle">
                        <IconButton onClick={() => navigate(`/logs/${invoice.batchNo}`)}>
                          <DescriptionIcon fontSize="medium" className="text-blue-600 cursor-pointer" />
                        </IconButton>
                      </td>
                      <td className="align-middle">
                        {invoice.batchNo || "--"}
                      </td>
                      <td className="align-middle">
                        {invoice.vendorName || "--"}
                      </td>
                      <td className="align-middle">
                        {invoice.caseCount || "--"}
                      </td>
                      <td className="align-middle">
                        {invoice.totalRepairCharges || "--"}
                      </td>
                      <td className="align-middle">
                        {invoice.totalServiceCharges || "--"}
                      </td>
                      <td className="align-middle">
                        {invoice.gst || "--"}
                      </td>
                      <td className="align-middle">

                        {invoice.finalAmount || "--"}
                      </td>
                      <td className="align-middle">
                        {invoice.invoice ? (
                          /\.(jpe?g|png|gif|bmp|webp)$/i.test(invoice.invoice) ? (
                            <a
                              href={invoice.invoice}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                            >
                              <img
                                src={invoice.invoice}
                                alt="Invoice Image"
                                style={{ height: "40px", width: "40px", objectFit: "cover", borderRadius: "4px", cursor: "pointer" }}
                              />
                            </a>
                          ) : (
                            <a
                              href={invoice.invoice}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                            >
                              <img
                                src={pdfimage}
                                alt="Download PDF"
                                style={{ height: "24px", cursor: "pointer" }}
                              />
                            </a>
                          )
                        ) : (
                          "--"
                        )}
                      </td>

                      <td className="align-middle">
                        {(() => {
                          const displayStatus = invoice.invoiceStatus || "N/A";
                          const statusKey = displayStatus.trim();
                          const statusStyle = statusColor[statusKey] || {
                            color: "#6B7280",
                            backgroundColor: "#F3F4F6",
                          };

                          return (
                            <span
                              className="vendore_invoice_status px-3 py-1 rounded-pill"
                              style={{
                                display: "inline-block",
                                border: `1px solid ${statusStyle.color}`,
                                color: statusStyle.color,
                                backgroundColor: statusStyle.backgroundColor,
                                fontWeight: 600,
                                fontSize: "13px",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {displayStatus}
                            </span>
                          );
                        })()}
                      </td>
                      <td>{invoice.networkStatus}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className="text-center py-4">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          <div className="d-flex justify-content-between align-items-center pagination-container network_previous">
            <button
              className="network_previous"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="network_previous"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <Modal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        centered
        backdrop="static"
        className="hold_reason_modal batch_popup_modal"
        size="lg"
      >
        <Modal.Header closeButton style={{ backgroundColor: "#e6f0ff" }}>
          <Modal.Title className="fw-bold text-dark" style={{ fontSize: "18px" }}>
            Add Payment Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handlePaymentSubmit} className="invoice_form">
            <div className="row">
              {/* Batch No (Display only) */}
              <div className="col-md-6 mb-3">
                <Form.Label className="fw-semibold text-secondary">Batch Number</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedBatch?.batchNo || "--"}
                  disabled
                  className="bg-light border-secondary"
                  style={{ fontWeight: 600, color: "#495057" }}
                />
              </div>

              {/* Invoice Number (Display only) */}
              <div className="col-md-6 mb-3">
                <Form.Label className="fw-semibold text-secondary">Invoice Number</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedBatch?.invoiceNo || "--"}
                  disabled
                  className="bg-light border-secondary"
                  style={{ fontWeight: 600, color: "#495057" }}
                />
              </div>
            </div>

            <div className="row">
              {/* Payment Date */}
              <div className="col-md-6 mb-3">
                <Form.Label className="fw-semibold text-dark">
                  Payment Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="paymentDate"
                  value={paymentForm.paymentDate}
                  onChange={handleFormChange}
                  isInvalid={formErrors.paymentDate}
                  className="border-dark"
                />
                <Form.Control.Feedback type="invalid">
                  Payment Date is required.
                </Form.Control.Feedback>
              </div>

              {/* Transaction Number */}
              <div className="col-md-6 mb-3">
                <Form.Label className="fw-semibold text-dark">
                  Transaction Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="transactionNo"
                  placeholder="Enter Transaction Number"
                  value={paymentForm.transactionNo}
                  onChange={handleFormChange}
                  isInvalid={formErrors.transactionNo}
                  className="border-dark"
                />
                <Form.Control.Feedback type="invalid">
                  Transaction Number is required.
                </Form.Control.Feedback>
              </div>
            </div>

            <div className="row">
              {/* Bank Reference Number */}
              <div className="col-md-6 mb-3">
                <Form.Label className="fw-semibold text-dark">
                  Bank Reference Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="bankRefNo"
                  placeholder="Enter Bank Reference Number"
                  value={paymentForm.bankRefNo}
                  onChange={handleFormChange}
                  isInvalid={formErrors.bankRefNo}
                  className="border-dark"
                />
                <Form.Control.Feedback type="invalid">
                  Bank Reference Number is required.
                </Form.Control.Feedback>
              </div>

              {/* Amount Paid */}
              <div className="col-md-6 mb-3">
                <Form.Label className="fw-semibold text-dark">
                  Amount Paid <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="amountPaid"
                  placeholder="Enter Amount Paid"
                  value={paymentForm.amountPaid}
                  onChange={handleFormChange}
                  isInvalid={formErrors.amountPaid}
                  className="border-dark"
                />
                <Form.Control.Feedback type="invalid">
                  Amount Paid is required.
                </Form.Control.Feedback>
              </div>
            </div>

            <div className="row">
              {/* Payment Mode */}
              <div className="col-md-6 mb-3">
                <Form.Label className="fw-semibold text-dark">
                  Payment Mode <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="paymentMode"
                  value={paymentForm.paymentMode}
                  onChange={handleFormChange}
                  isInvalid={formErrors.paymentMode}
                  className="border-dark"
                >
                  <option value="NEFT">NEFT</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  Payment Mode is required.
                </Form.Control.Feedback>
              </div>

              {/* Remarks */}
              <div className="col-md-6 mb-3">
                <Form.Label className="fw-semibold text-dark">
                  Remarks <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="remarks"
                  placeholder="Enter Remarks"
                  value={paymentForm.remarks}
                  onChange={handleFormChange}
                  isInvalid={formErrors.remarks}
                  className="border-dark"
                />
                <Form.Control.Feedback type="invalid">
                  Remarks are required.
                </Form.Control.Feedback>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button
                variant="secondary"
                onClick={() => setShowPaymentModal(false)}
                style={{ borderRadius: "4px" }}
              >
                Close
              </Button>
              <Button
                type="submit"
                variant="primary"
                style={{ backgroundColor: "#8000d7", border: "none", borderRadius: "4px" }}
              >
                Submit Payment
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default ApprovedBatches;
